// The Basement overlay (2026-10-30, docs/plans/basement-overlay.md): a picture over the whole view that fades in as
// this visitor nears the room's centre (rooms.json `overlay`), split into its red, green and blue channels, each a
// layer of its own that drifts slightly apart from the others (overlay.css). The layers add up to the image when they
// line up (screen blend on disjoint channels). Only this client's own distance counts.
//
// Opacity: max_opacity * smoothstep(outer_m .. inner_m) of the distance (curve.ts), glided per frame with the
// time constant glide_s. At 0 the element is hidden (display none): nothing is drawn or composited and no animation
// runs. A new image fades in over 0.6 s while the old one fades out.
//
// Noise (2026-10-30, user: "animated with noise"): each channel layer jitters (small random offsets in whole image
// pixels, at 11-13 Hz, over a slow drift that jumps a block every few seconds), and a tiled grey noise texture with
// square pixels lies over the image (overlay blend), jumping to a random offset at 13 Hz. One timer at 13 Hz sets the
// four transforms, only while the overlay is visible. (CSS keyframe animations with steps() cost the main thread
// 8-9 ms per second of style updates in Chrome; the timer costs less.) The image is drawn at a low resolution
// (rooms.json pixel_px) and scaled up with hard pixel edges. With reduced motion nothing moves (overlay.css). An
// animated image shows its first frame (playing it was measured and dropped: web/NOTES.md).

import './overlay.css';
import type { RoomOverlay } from '@lr3d/shared';
import { glide, overlayAmount } from './curve';

/** Opacity below this with a target of 0 counts as 0 (the layers are hidden). */
const OFF = 0.002;
/** Crossfade between two images (ms; overlay.css .ro-group transition). */
const CROSSFADE_MS = 600;

interface Group {
  el: HTMLDivElement;
  hash: string;
  /** the three channel layers (green, red, blue) and their jitter wrappers */
  layers: CanvasRenderingContext2D[];
  jits: HTMLDivElement[];
}
const CHANNELS = [['g', '#00ff00'], ['r', '#ff0000'], ['b', '#0000ff']] as const;

/** Jitter per channel: the largest offset in blocks and the rate of new offsets (Hz, at most MOTION_HZ). */
const JITTER: Record<'g' | 'r' | 'b', { max: number; hz: number }> = { g: { max: 1, hz: 11 }, r: { max: 2, hz: 13 }, b: { max: 2, hz: 12 } };
/** The motion timer's rate (Hz). */
const MOTION_HZ = 13;
/** The image's long side in pixels when rooms.json gives no pixel_px. */
const DEFAULT_PIXEL_PX = 320;
/** Noise texture: tile size (texels) and CSS px per texel. */
const NOISE = { desktop: { tile: 128, px: 3 }, mobile: { tile: 96, px: 4 } };

/**
 * Slow drift per channel in whole blocks: each position held for `s / at.length` seconds, then a jump of a block or
 * two. Red sits left of green, blue right.
 */
const DRIFT: Record<'g' | 'r' | 'b', { s: number; at: [number, number][] }> = {
  g: { s: 29, at: [[0, 0], [1, 0], [0, 1], [0, 0], [-1, 0]] },
  r: { s: 17, at: [[-1, 0], [-2, -1], [-1, 1], [-2, 0], [-1, -1]] },
  b: { s: 23, at: [[1, 0], [2, 1], [1, -1], [2, 0], [1, 1]] },
};

/** A tile of grey noise around mid grey (neutral under the overlay blend) as a data URL. */
function noiseTile(n: number): string {
  const cv = document.createElement('canvas');
  cv.width = cv.height = n;
  const ctx = cv.getContext('2d')!;
  const d = ctx.createImageData(n, n);
  for (let i = 0; i < n * n; i++) {
    const v = Math.round(128 + (Math.random() * 2 - 1) * 110);
    d.data[i * 4] = d.data[i * 4 + 1] = d.data[i * 4 + 2] = v;
    d.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(d, 0, 0);
  return cv.toDataURL('image/png');
}

export class ScreenOverlay {
  readonly el: HTMLDivElement;
  /** The opacity shown now and the one it glides toward. */
  opacity = 0;
  target = 0;
  /** The amount (0..1) at the player's position: the brown noise follows it (noise_gain * amount). */
  amount = 0;
  /** Largest opacity change in one frame since the last reset, frames drawn while visible (tests, HUD). */
  readonly stats = { maxStep: 0, visibleFrames: 0, motionSteps: 0 };
  private config: RoomOverlay | null = null;
  /** The group on top (the current image) and those fading out. */
  private current: Group | null = null;
  private wantHash: string | undefined;
  private seq = 0;
  /** The file shown now (undefined = none) or the file that failed to load. */
  onShown: (hash: string | undefined, failed?: string) => void = () => {};

  /** The noise texture over the image (stays the last child). */
  private noise: HTMLDivElement;
  /** The block size needs measuring again (a new image, a resize). */
  private measure = true;
  /** One image pixel on screen (CSS px, whole). */
  private blkPx = 4;
  private noisePx: number;
  private tilePx: number;
  /** The motion timer (drift, jitter, noise), running only while the overlay is visible. */
  private motion = 0;
  private jitter: Record<string, [number, number]> = { g: [0, 0], r: [0, 0], b: [0, 0] };
  private readonly reduced = matchMedia('(prefers-reduced-motion: reduce)');

  constructor(private opts: { mobile: boolean }) {
    const el = (this.el = document.createElement('div'));
    el.id = 'room-overlay';
    el.setAttribute('aria-hidden', 'true');
    el.hidden = true;
    const nz = opts.mobile ? NOISE.mobile : NOISE.desktop;
    const tilePx = nz.tile * nz.px;
    this.noisePx = nz.px;
    this.tilePx = tilePx;
    this.noise = document.createElement('div');
    this.noise.className = 'ro-noise';
    this.noise.style.backgroundImage = `url(${noiseTile(nz.tile)})`;
    this.noise.style.backgroundSize = `${tilePx}px ${tilePx}px`;
    this.noise.style.setProperty('--tile', `${tilePx}px`);
    this.noise.hidden = true;
    el.append(this.noise);
    addEventListener('resize', () => { this.measure = true; });
    // after the canvas, before the labels and the UI (their z-index is higher anyway)
    const canvas = document.getElementById('c');
    canvas?.after(el);
    if (!canvas) document.body.append(el);
  }

  /** The room's overlay setting (a room switch); null = none: the overlay fades out and its image is dropped. */
  setConfig(c: RoomOverlay | null): void {
    this.config = c;
  }

  get active(): RoomOverlay | null {
    return this.config;
  }

  /**
   * The image to show (null = none). Loads it, makes the three channel layers, crossfades from the old one. An
   * animated GIF or WebP shows its first frame: playing it was measured and dropped (web/NOTES.md, Basement overlay).
   */
  setImage(url: string | null, hash?: string): void {
    const s = ++this.seq;
    this.wantHash = url ? hash : undefined;
    if (!url || !hash) {
      this.swap(null);
      this.onShown(undefined);
      return;
    }
    if (this.current?.hash === hash) { this.onShown(hash); return; }
    const failed = (e: unknown) => {
      if (s !== this.seq) return;
      console.warn('[overlay] image failed', url, e);
      this.onShown(this.current?.hash, hash);
    };
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.src = url;
    img.decode().then(() => {
      if (s !== this.seq) return;
      const k = Math.min(1, this.longSide() / Math.max(img.naturalWidth, img.naturalHeight));
      const g = this.makeGroup(Math.max(1, Math.round(img.naturalWidth * k)), Math.max(1, Math.round(img.naturalHeight * k)), hash);
      this.paint(g, img);
      this.swap(g);
      this.onShown(hash);
    }).catch(failed);
  }

  /**
   * One frame: the player at (x, z), dt seconds since the last frame. Returns true while the opacity is still
   * moving (the caller keeps requesting frames).
   */
  update(x: number, z: number, dt: number): boolean {
    const c = this.config;
    this.amount = c ? overlayAmount(c, x, z) : 0;
    this.target = c && this.current ? c.max_opacity * this.amount : 0;
    const before = this.opacity;
    let o = glide(before, this.target, dt, c?.glide_s ?? 0.45);
    if (this.target === 0 && o < OFF) o = 0;
    this.opacity = o;
    this.stats.maxStep = Math.max(this.stats.maxStep, Math.abs(o - before));
    const show = o > 0;
    if (show) {
      this.stats.visibleFrames++;
      const v = o.toFixed(4);
      if (this.el.style.opacity !== v) this.el.style.opacity = v;
    }
    if (this.el.hidden === show) {
      this.el.hidden = !show;
    }
    if (show && this.current && !this.reduced.matches) this.startMotion();
    else if (this.motion) this.stopMotion();
    if (show && this.measure && this.current) this.setBlock();
    // the room has no overlay any more and it has faded out: free the layers
    if (!show && !c && this.current) this.swap(null);
    return Math.abs(o - this.target) > 1e-4;
  }

  /**
   * The size of one image pixel on screen (CSS px, rounded to a whole px so block offsets stay on whole pixels): the
   * layers are cover-fitted to the view plus 8 %.
   */
  private setBlock(): void {
    const cv = this.current?.el.querySelector('canvas');
    const w = this.el.clientWidth, h = this.el.clientHeight;
    if (!cv || !w || !h) return;
    this.measure = false;
    const k = Math.max((w * 1.08) / cv.width, (h * 1.08) / cv.height);
    this.blkPx = Math.max(1, Math.round(k));
    this.el.style.setProperty('--blk', `${this.blkPx}px`);
  }

  private startMotion(): void {
    if (this.motion) return;
    this.motion = window.setInterval(() => this.step(), 1000 / MOTION_HZ);
    this.step();
  }

  private stopMotion(): void {
    clearInterval(this.motion);
    this.motion = 0;
  }

  /** One motion step: each channel's drift position plus its jitter, and a new noise offset (four style writes). */
  private step(): void {
    const g = this.current;
    if (!g || document.hidden) return;
    const t = performance.now() / 1000, b = this.blkPx;
    const rnd = (max: number) => { const u = Math.random(); const n = u < 0.45 ? 0 : u < 0.9 ? 1 : max; return Math.random() < 0.5 ? -n : n; };
    g.jits.forEach((el, i) => {
      const c = CHANNELS[i]![0];
      const d = DRIFT[c], j = JITTER[c];
      if (Math.random() < j.hz / MOTION_HZ) this.jitter[c] = [rnd(j.max), Math.random() < 0.7 ? 0 : rnd(1)];
      const [dx, dy] = d.at[Math.floor(t / (d.s / d.at.length)) % d.at.length]!;
      const [jx, jy] = this.jitter[c]!;
      el.style.transform = `translate(${(dx + jx) * b}px, ${(dy + jy) * b}px)`;
    });
    const n = this.noisePx, cells = this.tilePx / n;
    this.noise.style.transform = `translate(${-Math.floor(Math.random() * cells) * n}px, ${-Math.floor(Math.random() * cells) * n}px)`;
    this.stats.motionSteps++;
  }

  /** The image's long side in the layers: rooms.json pixel_px (0 = at most 1600 px, 1024 mobile). */
  private longSide(): number {
    const px = this.config?.pixel_px ?? DEFAULT_PIXEL_PX;
    return px > 0 ? px : this.opts.mobile ? 1024 : 1600;
  }

  /** The three channel layers (w x h), cover-fitted by CSS and scaled up without smoothing, each in a jitter wrapper. */
  private makeGroup(w: number, h: number, hash: string): Group {
    const el = document.createElement('div');
    el.className = 'ro-group';
    const layers: CanvasRenderingContext2D[] = [];
    const jits: HTMLDivElement[] = [];
    // green first (drawn normally), red and blue added over it (screen)
    for (const [c] of CHANNELS) {
      const jit = document.createElement('div');
      jit.className = 'ro-jit';
      const cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      cv.className = `ro-layer ro-${c}`;
      jit.append(cv);
      const ctx = cv.getContext('2d', { alpha: false })!;
      ctx.imageSmoothingQuality = 'high'; // a downscale averages; the upscale (CSS) is nearest
      layers.push(ctx);
      jits.push(jit);
      el.append(jit);
    }
    return { el, hash, layers, jits };
  }

  /** Draw `src` into the layers, each keeping one channel (multiplied by pure red, green or blue). */
  private paint(g: Group, src: CanvasImageSource): void {
    g.layers.forEach((ctx, i) => {
      const { width: w, height: h } = ctx.canvas;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(src, 0, 0, w, h);
      ctx.globalCompositeOperation = 'multiply';
      ctx.fillStyle = CHANNELS[i]![1];
      ctx.fillRect(0, 0, w, h);
    });
  }

  /** Make `g` the shown image: it fades in over the old one, which fades out and is removed. */
  private swap(g: Group | null): void {
    const old = this.current;
    this.current = g;
    this.noise.hidden = !g;
    this.measure = true;
    const visible = !this.el.hidden && this.opacity > 0;
    if (old) {
      if (visible) {
        old.el.style.opacity = '0';
        setTimeout(() => old.el.remove(), CROSSFADE_MS + 100);
      } else old.el.remove();
    }
    if (!g) return;
    if (visible) {
      g.el.style.opacity = '0';
      this.el.insertBefore(g.el, this.noise);
      void g.el.offsetWidth; // style the 0 first, so the transition runs from it
      g.el.style.opacity = '1';
    } else {
      g.el.style.opacity = '1';
      this.el.insertBefore(g.el, this.noise);
    }
  }

  /** The hash wanted now (tests). */
  get wanted(): string | undefined {
    return this.wantHash;
  }
}
